import { useCallback, useLayoutEffect, useRef } from "react";
import { DownOutlined, LeftOutlined, RightOutlined, UpOutlined } from "@ant-design/icons";
import "./scroll-indicator.styles.scss";

const MIN_THUMB_SIZE = 32;
export const SCROLL_INDICATOR_GUTTER_SIZE = 12;
export const SCROLL_INDICATOR_BUTTON_SIZE = 18;

export function calculateAvailableTrackSize(trackSize) {
  return Math.max(0, trackSize - SCROLL_INDICATOR_BUTTON_SIZE * 2);
}

export function getEffectiveScrollSize({ scrollSize, oppositeAxisActive = false, managed = false }) {
  if (!managed || !oppositeAxisActive) return scrollSize;
  return Math.max(0, scrollSize - SCROLL_INDICATOR_GUTTER_SIZE);
}

export function calculateScrollThumb({
  clientSize,
  scrollSize,
  scrollPosition,
  trackSize,
  clientHeight,
  scrollHeight,
  scrollTop,
  trackHeight
}) {
  const resolvedClientSize = clientSize ?? clientHeight;
  const resolvedScrollSize = scrollSize ?? scrollHeight;
  const resolvedScrollPosition = scrollPosition ?? scrollTop;
  const resolvedTrackSize = trackSize ?? trackHeight;

  if (!resolvedClientSize || resolvedScrollSize <= resolvedClientSize || !resolvedTrackSize) {
    return { hidden: true, height: 0, top: 0 };
  }

  const height = Math.min(
    resolvedTrackSize,
    Math.max(MIN_THUMB_SIZE, (resolvedClientSize / resolvedScrollSize) * resolvedTrackSize)
  );
  const maxThumbTop = Math.max(0, resolvedTrackSize - height);
  const maxScrollTop = Math.max(1, resolvedScrollSize - resolvedClientSize);

  return {
    hidden: false,
    height,
    top: Math.min(maxThumbTop, Math.max(0, (resolvedScrollPosition / maxScrollTop) * maxThumbTop))
  };
}

export default function ScrollIndicator({
  axis = "y",
  className = "",
  observeMutations = false,
  style,
  targetElement,
  targetRef,
  viewport = false
}) {
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const metricsRef = useRef(null);
  const dragRef = useRef(null);
  const animationFrameRef = useRef(null);

  const getScrollTarget = useCallback(
    () => (viewport ? document.scrollingElement || document.documentElement : targetElement || targetRef?.current),
    [targetElement, targetRef, viewport]
  );

  const updateThumb = useCallback(() => {
    const target = getScrollTarget();
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!target || !track || !thumb) return;

    const horizontal = axis === "x";
    const clientSize = horizontal ? target.clientWidth : viewport ? window.innerHeight : target.clientHeight;
    const oppositeAxis = horizontal ? "y" : "x";
    const scrollSize = getEffectiveScrollSize({
      scrollSize: horizontal ? target.scrollWidth : target.scrollHeight,
      oppositeAxisActive: target.classList.contains(`scroll-indicator-target-active-${oppositeAxis}`),
      managed: target.classList.contains("scroll-indicator-managed")
    });
    const metrics = calculateScrollThumb({
      clientSize,
      scrollSize,
      scrollPosition: horizontal ? target.scrollLeft : target.scrollTop,
      trackSize: calculateAvailableTrackSize((horizontal ? track.clientWidth : track.clientHeight) || clientSize)
    });
    metricsRef.current = metrics;
    track.hidden = metrics.hidden;
    thumb.hidden = metrics.hidden;
    if (horizontal) {
      thumb.style.width = `${metrics.height}px`;
      thumb.style.transform = `translateX(${metrics.top}px)`;
    } else {
      thumb.style.height = `${metrics.height}px`;
      thumb.style.transform = `translateY(${metrics.top}px)`;
    }
    if (viewport) document.documentElement.classList.toggle("viewport-scrollable", !metrics.hidden);
    else {
      target.classList.toggle("scroll-indicator-target-active", !metrics.hidden);
      target.classList.toggle(`scroll-indicator-target-active-${axis}`, !metrics.hidden);
    }
  }, [axis, getScrollTarget, viewport]);

  const scheduleUpdate = useCallback(() => {
    if (animationFrameRef.current !== null) return;

    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = null;
      updateThumb();
    });
  }, [updateThumb]);

  useLayoutEffect(() => {
    const target = getScrollTarget();
    if (!target) return undefined;

    const scrollSource = viewport ? window : target;
    const handleUpdate = () => updateThumb();
    handleUpdate();
    scrollSource.addEventListener("scroll", handleUpdate, { passive: true });
    window.addEventListener("resize", handleUpdate, { passive: true });

    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(scheduleUpdate);
    resizeObserver?.observe(target);
    if (trackRef.current && trackRef.current !== target) resizeObserver?.observe(trackRef.current);
    if (viewport && document.body !== target) resizeObserver?.observe(document.body);

    const mutationObserver =
      observeMutations && typeof MutationObserver !== "undefined" ? new MutationObserver(handleUpdate) : null;
    mutationObserver?.observe(target, { childList: true, subtree: true });

    return () => {
      scrollSource.removeEventListener("scroll", handleUpdate);
      window.removeEventListener("resize", handleUpdate);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (viewport) document.documentElement.classList.remove("viewport-scrollable");
      else {
        target.classList.remove("scroll-indicator-target-active");
        target.classList.remove(`scroll-indicator-target-active-${axis}`);
      }
    };
  }, [axis, getScrollTarget, observeMutations, scheduleUpdate, updateThumb, viewport]);

  const handlePointerDown = useCallback(
    (event) => {
      const target = getScrollTarget();
      const track = trackRef.current;
      const metrics = metricsRef.current;
      if (!target || !track || !metrics || metrics.hidden) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragRef.current = {
        startPointerPosition: axis === "x" ? event.clientX : event.clientY,
        startScrollPosition: axis === "x" ? target.scrollLeft : target.scrollTop,
        trackSize: calculateAvailableTrackSize(
          (axis === "x" ? track.clientWidth : track.clientHeight) ||
            (axis === "x" ? target.clientWidth : viewport ? window.innerHeight : target.clientHeight)
        )
      };
    },
    [axis, getScrollTarget, viewport]
  );

  const handleScrollButtonPointerDown = useCallback(
    (direction, event) => {
      const target = getScrollTarget();
      if (!target) return;

      event.preventDefault();
      event.stopPropagation();
      const horizontal = axis === "x";
      const clientSize = horizontal ? target.clientWidth : viewport ? window.innerHeight : target.clientHeight;
      const distance = direction * Math.max(32, clientSize * 0.1);
      if (horizontal) target.scrollLeft += distance;
      else target.scrollTop += distance;
    },
    [axis, getScrollTarget, viewport]
  );

  const handlePointerMove = useCallback(
    (event) => {
      const drag = dragRef.current;
      const metrics = metricsRef.current;
      const target = getScrollTarget();
      if (!drag || !metrics || !target) return;

      const maxThumbPosition = Math.max(1, drag.trackSize - metrics.height);
      const clientSize = axis === "x" ? target.clientWidth : viewport ? window.innerHeight : target.clientHeight;
      const oppositeAxis = axis === "x" ? "y" : "x";
      const scrollSize = getEffectiveScrollSize({
        scrollSize: axis === "x" ? target.scrollWidth : target.scrollHeight,
        oppositeAxisActive: target.classList.contains(`scroll-indicator-target-active-${oppositeAxis}`),
        managed: target.classList.contains("scroll-indicator-managed")
      });
      const maxScrollPosition = Math.max(0, scrollSize - clientSize);
      const pointerPosition = axis === "x" ? event.clientX : event.clientY;
      const nextScrollPosition =
        drag.startScrollPosition +
        ((pointerPosition - drag.startPointerPosition) / maxThumbPosition) * maxScrollPosition;

      if (axis === "x") target.scrollLeft = nextScrollPosition;
      else target.scrollTop = nextScrollPosition;
    },
    [axis, getScrollTarget, viewport]
  );

  const handlePointerUp = useCallback((event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragRef.current = null;
  }, []);

  return (
    <div
      ref={trackRef}
      style={style}
      className={["scroll-indicator", `scroll-indicator--${axis}`, className].filter(Boolean).join(" ")}
      aria-hidden="true"
    >
      <button
        type="button"
        tabIndex={-1}
        className="scroll-indicator-button scroll-indicator-button--start"
        onPointerDown={(event) => handleScrollButtonPointerDown(-1, event)}
      >
        {axis === "x" ? <LeftOutlined /> : <UpOutlined />}
      </button>
      <button
        type="button"
        tabIndex={-1}
        className="scroll-indicator-button scroll-indicator-button--end"
        onPointerDown={(event) => handleScrollButtonPointerDown(1, event)}
      >
        {axis === "x" ? <RightOutlined /> : <DownOutlined />}
      </button>
      <div
        ref={thumbRef}
        className="scroll-indicator-thumb"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
    </div>
  );
}
